import React, { Fragment } from 'react'
import cx from 'classnames'
import { delve } from '@mylo/dlv-and-dset'

import './FlowField.css'

const FlowFieldValidation = ({
  touched,
  errorText,
  warningText,
  hintText,
  successText,
  radioGroup = false,
  checkboxGroup = false,
  hasHintText = !!hintText,
  hasSuccessText = !!successText,
  hasWarningText = !!warningText,
  hasErrorText = !!errorText,
  showError = !!touched && hasErrorText,
  showWarning = !!touched && !showError && hasWarningText,
  showSuccess = !showError && !showWarning && hasSuccessText,
  showHint = !showError && !showWarning && !showSuccess && hasHintText
}) => (
  <Fragment>
    {showHint ? (
      <span
        className={cx('flow__field--hint', {
          'flow__field--hint-radio': radioGroup,
          'flow__field--hint-checkbox': checkboxGroup
        })}>
        {hintText}
      </span>
    ) : null}
    {showError ? (
      <span
        className={cx('flow__field--error', {
          'flow__field--error-radio': radioGroup,
          'flow__field--error-checkbox': checkboxGroup
        })}>
        {errorText}
      </span>
    ) : null}
    {showWarning ? (
      <span
        className={cx('flow__field--warning', {
          'flow__field--warning-radio': radioGroup,
          'flow__field--warning-checkbox': checkboxGroup
        })}>
        {warningText}
      </span>
    ) : null}
    {showSuccess ? (
      <span
        className={cx('flow__field--success', {
          'flow__field--success-radio': radioGroup,
          'flow__field--success-checkbox': checkboxGroup
        })}>
        {successText}
      </span>
    ) : null}
  </Fragment>
)

const FlowField = ({
  input = {},
  meta = {},
  children,
  label,
  labelClassName,
  fieldClassName,
  orange,
  green,
  hintText,
  successText,
  touched = meta.touched,
  warningText = meta.warning,
  errorText = meta.error,
  ...props
}) => {
  const fieldClassNames = cx(
    'flow__field',
    {
      'flow__field--empty': !input.value,
      'flow__field--invalid': touched && !!errorText,
      'flow__field--orange': orange,
      'flow__field--green': green
    },
    fieldClassName
  )

  if (props.radioGroup) {
    return (
      <fieldset className={fieldClassNames}>
        <legend className={labelClassName}>{label}</legend>
        <span
          className={cx('flow__field--group', {
            horizontal: props.horizontal === true
          })}>
          {React.Children.map(children, (child, index) => {
            const id = `${input.name}-${child.props.value}`
            return (
              <span className={getWrapperClass(index)}>
                {React.cloneElement(child, {
                  ...input,
                  ...child.props,
                  className: cx('flow__field--radio', child.props.className),
                  name: input.name,
                  checked:
                    input.value.toString() === child.props.value.toString(),
                  id
                })}
                <label htmlFor={id}>
                  <span>{child.props.name}</span>
                </label>
              </span>
            )
          })}
        </span>
        <FlowFieldValidation
          touched={touched}
          hintText={hintText}
          successText={successText}
          warningText={warningText}
          errorText={errorText}
          radioGroup
        />
      </fieldset>
    )
  }

  if (props.checkboxGroup) {
    return (
      <fieldset className={fieldClassNames}>
        <legend className={labelClassName}>{label}</legend>
        <span
          className={cx('flow__field--group', {
            horizontal: props.horizontal === true
          })}>
          {React.Children.map(children, (child, index) => {
            const fieldProps = delve(props, child.props.value)
            const id = `${fieldProps.input.name}-${child.props.value}`
            return (
              <span className={getWrapperClass(index, fieldProps.meta)}>
                {React.cloneElement(child, {
                  ...fieldProps.input,
                  ...child.props,
                  className: cx('flow__field--checkbox', child.props.className),
                  name: fieldProps.input.name,
                  checked: !!fieldProps.input.value,
                  id
                })}
                <label htmlFor={id}>
                  <span>{child.props.name}</span>
                  <span />
                </label>
              </span>
            )
          })}
        </span>
        <FlowFieldValidation
          touched={touched}
          hintText={hintText}
          successText={successText}
          warningText={warningText}
          errorText={errorText}
          checkboxGroup
        />
      </fieldset>
    )
  }

  const relayProps = { ...input, ...props, id: input.name }
  if (typeof children === 'function') {
    return (
      <div className={fieldClassNames}>
        <label htmlFor={input.name} className={labelClassName}>
          {label}
        </label>
        {children({ ...relayProps, ...children.props }, meta)}
        <FlowFieldValidation
          touched={touched}
          hintText={hintText}
          successText={successText}
          warningText={warningText}
          errorText={errorText}
        />
      </div>
    )
  }

  return (
    <div className={fieldClassNames}>
      <label htmlFor={input.name} className={labelClassName}>
        {label}
      </label>
      {React.cloneElement(children, { ...relayProps, ...children.props })}
      <FlowFieldValidation
        touched={touched}
        hintText={hintText}
        successText={successText}
        warningText={warningText}
        errorText={errorText}
      />
    </div>
  )
}

export default FlowField

function getWrapperClass (index, { touched, error } = {}) {
  return cx({
    'flow__field--invalid': touched && !!error,
    'flow__field--group-orange': index % 3 === 1,
    'flow__field--group-green': index % 3 === 2
  })
}
